import React, { useEffect, useState, useCallback, useRef } from "react";
import { Dropdown, Modal, Button, Tabs, Tab } from "react-bootstrap";
import { Formik, Field, Form as FormikForm, ErrorMessage } from "formik";
import * as Yup from "yup";
import toast from "react-hot-toast";
import Spinner from "../components/Common/Spinner";
import {
  addFile,
  createNewAdmin,
  deleteAdmin,
  getCategory,
  getSubAdmins,
  getUrl,
  updateAdminInfo,
  updateAdminStatus,
  vendorCategory,
  vendorRequest,
} from "../../services/Admin/adminServices";
import Pagination from "../components/Pagination";
import { useFetcher, useNavigate } from "react-router-dom";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";

const Users = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState(null);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage] = useState(10);
  const [totalItems, setTotalItems] = useState(0);
  const [activeTab, setActiveTab] = useState("User");
  const [type, setType] = useState("");

  const [categories, setCategories] = useState([]);

  // 🌟 Added state for the Confirmation Modal
  const [confirmState, setConfirmState] = useState({
    show: false,
    action: null,
    id: null,
    status: null,
    message: "",
  });

  // 🌟 Added state for the Change Category Modal
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedVendorForCategory, setSelectedVendorForCategory] = useState(null);
  const [newCategory, setNewCategory] = useState("");
  const [updatingCategory, setUpdatingCategory] = useState(false);

  const navigate = useNavigate();
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState("");
  const fileInputRef = useRef(null);
  const [imageLoading, setImageLoading] = useState(false);
  const [submit, setSubmit] = useState(false);
  const svg1 = (
    <svg width="20px" height="20px" viewBox="0 0 24 24" version="1.1">
      <g stroke="none" strokeWidth="1" fill="none" fillRule="evenodd">
        <rect x="0" y="0" width="24" height="24"></rect>
        <circle fill="#000000" cx="12" cy="5" r="2"></circle>
        <circle fill="#000000" cx="12" cy="12" r="2"></circle>
        <circle fill="#000000" cx="12" cy="19" r="2"></circle>
      </g>
    </svg>
  );

  const fetchAdmins = useCallback(async () => {
    try {
      setLoading(true);
      const response = await getSubAdmins(
        currentPage,
        itemsPerPage,
        activeTab,
        type,
        debouncedSearch,
        startDate,
        endDate
      );
      setAdmins(response?.data?.data?.data || []);
      setTotalItems(response?.data?.data?.total || 0);
    } catch (error) {
      console.error(error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    itemsPerPage,
    activeTab,
    debouncedSearch,
    startDate,
    endDate,
    type,
  ]);

  useEffect(() => {
    fetchAdmins();
  }, [fetchAdmins]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedSearch(search.trim());
    }, 500);

    return () => clearTimeout(timeoutId);
  }, [search]);

  const fetchCategories = async () => {
    try {
      const res = await getCategory(0, 100);
      setCategories(res?.data?.data?.data || []);
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    setCurrentPage(0);
  }, [debouncedSearch, activeTab, startDate, endDate, type]);

  useEffect(() => {
    setSearch("");
    setCurrentPage(0);
    setStartDate("");
    setEndDate("");
    setType("");
  }, [activeTab]);

  const getUrlImage = async (img) => {
    try {
      return await getUrl(img);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = async (id) => {
    try {
      await deleteAdmin(id);
      toast.success("Successfully deleted");
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to delete user");
      console.error(error);
    }
  };

  const handlestatus = async (id) => {
    try {
      const payload = { userId: id };
      await updateAdminStatus(payload);
      toast.success("Status updated successfully");
      fetchAdmins();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to update status");
      console.error(error);
    }
  };

  const handleFileUpload = async (e, fieldName, setFieldValue) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      setUploading(true);
      const formData = new FormData();
      formData.append("file", file);
      const res = await addFile(formData);

      if (res) {
        setPreview(URL.createObjectURL(file));
        setFieldValue("image", res?.data?.data?.fileUrl);
      }
    } catch (error) {
      toast.error("File upload failed");
      setPreview("");
    } finally {
      setUploading(false);
    }
  };

  const openModal = async (admin = null) => {
    setSelectedAdmin(admin);
    setShowModal(true);

    if (admin?.image) {
      try {
        setImageLoading(true);
        const res = await getUrlImage(admin.image);
        setPreview(res?.data?.data);
      } catch (error) {
        console.error(error);
      } finally {
        setImageLoading(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedAdmin(null);
    setPreview("");

    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const AdminSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    image: Yup.string().required("image is required"),
  });

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const handleSubmit = async (values, { resetForm }) => {
    try {
      setSubmit(true);
      if (selectedAdmin) {
        const payload = {
          id: selectedAdmin._id,
          name: values.name,
          image: values.image,
        };
        await updateAdminInfo(payload);
        toast.success("Updated successfully");
      } else {
        await createNewAdmin(values);
        toast.success("Added successfully");
        resetForm();
      }
      fetchAdmins();
      setShowModal(false);
    } catch (error) {
      toast.error(error?.response?.data?.message || "Failed to save admin");
    } finally {
      setSubmit(false);
    }
  };

  const getCategoryColor = (category) => {
    const colors = [
      "primary",
      "success",
      "danger",
      "warning",
      "info",
      "dark",
      "secondary",
    ];

    let hash = 0;

    for (let i = 0; i < category.length; i++) {
      hash = category.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const isFilterActive = search || startDate || endDate || type;

  const handleRequest = async (status, id) => {
    try {
      const payload = {
        id: id,
        status: status,
      };
      await vendorRequest(payload);
      toast.success(`Request Successfully ${status}ed`);
      fetchAdmins();
    } catch (error) {
      console.error(error);
    }
  };

  const handleClear = () => {
    setSearch("");
    setStartDate("");
    setEndDate("");
    setType("");
  };

  // 🌟 Trigger Confirmation Modal
  const triggerConfirm = (action, id, status = null) => {
    let message = "";
    if (action === "delete") {
      message =
        "Are you sure you want to delete this record? This action cannot be undone.";
    } else if (action === "status") {
      message = `Are you sure you want to ${status.toLowerCase()} this account?`;
    } else if (action === "request") {
      message = `Are you sure you want to ${status.toLowerCase()} this vendor's request?`;
    }

    setConfirmState({ show: true, action, id, status, message });
  };

  // 🌟 Execute Action After Confirmation
  const confirmActionHandler = () => {
    const { action, id, status } = confirmState;
    if (action === "delete") {
      handleDelete(id);
    } else if (action === "status") {
      handlestatus(id);
    } else if (action === "request") {
      handleRequest(status, id);
    }
    setConfirmState({
      show: false,
      action: null,
      id: null,
      status: null,
      message: "",
    });
  };

  // 🌟 Open Category Modal
  const openCategoryModal = (vendor) => {
    setSelectedVendorForCategory(vendor);
    // Pre-select the existing category if it exists
    setNewCategory(vendor?.businessDetails?.category?._id || "");
    setShowCategoryModal(true);
  };

  // 🌟 Close Category Modal
  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    setSelectedVendorForCategory(null);
    setNewCategory("");
  };

  // 🌟 Handle Category Submit
  const handleCategorySubmit = async () => {
    if (!newCategory) {
      toast.error("Please select a category");
      return;
    }
    try {
      setUpdatingCategory(true);

      const payload = {
        id: selectedVendorForCategory._id,
        categoryId: newCategory
      }
      await vendorCategory(payload)
      // Example: await updateVendorCategory({ vendorId: selectedVendorForCategory._id, categoryId: newCategory });

      toast.success("Category updated successfully");
      fetchAdmins(); // Refresh the table data
      closeCategoryModal();
    } catch (error) {
      toast.error("Failed to update category");
      console.error(error);
    } finally {
      setUpdatingCategory(false);
    }
  };

  return (
    <div className="container mt-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 style={{ color: "#64CAE5" }}>Users</h2>
      </div>

      <div className="pb-2 d-flex flex-wrap justify-content-between gap-2 align-items-center mb-4">
        <div className="flex-grow-1">
          <label
            className="form-label mb-1"
            style={{ fontSize: "12px" }}
          ></label>
          <input
            type="text"
            style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            className="form-control w-100"
            placeholder="Search by name, email....."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>
            Start Date
          </label>
          <input
            type="date"
            style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            className="form-control"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
        </div>

        <div>
          <label className="form-label mb-1" style={{ fontSize: "12px" }}>
            End Date
          </label>
          <input
            type="date"
            style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            className="form-control"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>
        {activeTab === "Vendor" && (
          <div>
            <label
              className="form-label mb-1"
              style={{ fontSize: "12px" }}
            ></label>
            <select
              style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
              className="form-control w-100"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Select vendor type</option>
              {categories.map((cat, index) => (
                <option key={cat._id || index} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        )}

        {isFilterActive && (
          <div className="d-flex flex-column">
            <label className="form-label mb-1" style={{ fontSize: "12px" }}>
              &nbsp;
            </label>
            <button
              className="btn btn-outline-danger"
              onClick={handleClear}
              style={{ borderRadius: "0.5rem" }}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k)}
        className="mb-3"
      >
        {/* USERS TAB */}
        <Tab eventKey="User" title="Users">
          {loading ? (
            <Spinner />
          ) : (
            <div className="table-responsive">
              <table className="table table-striped align-middle shadow-sm">
                <thead className="table-light">
                  <tr>
                    <th>Sr No.</th>
                    <th>Name</th>
                    <th>Email</th>
                    <th>Phone Number</th>
                    <th>Joined Date</th>
                    <th>Status</th>
                    <th style={{ textAlign: "center" }}>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {admins?.length > 0 ? (
                    admins.map((admin, index) => (
                      <tr key={admin._id || index}>
                        <td>{currentPage * itemsPerPage + index + 1}</td>
                        <td>
                          {admin.name
                            ? `${admin.name
                              .charAt(0)
                              .toUpperCase()}${admin.name.slice(1)}`
                            : "_"}
                        </td>
                        <td>{admin.email ? admin.email : "_"}</td>
                        <td>
                          {admin.phoneNumber
                            ? `${admin.countryCode || ""} ${admin.phoneNumber}`
                            : "_"}
                        </td>
                        <td>
                          {new Date(admin.createdAt).toLocaleDateString(
                            "en-CA",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )}
                          {", "}
                          {new Date(admin.createdAt).toLocaleTimeString(
                            "en-CA",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${!admin.isBlocked ? "bg-success" : "bg-danger"
                              }`}
                          >
                            {!admin.isBlocked ? "Active" : "Suspended"}
                          </span>
                        </td>
                        <td style={{ textAlign: "center" }}>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="light"
                              id={`dropdown-basic-${index}`}
                              style={{ padding: "4px 8px" }}
                            >
                              {svg1}
                            </Dropdown.Toggle>
                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => openModal(admin)}>
                                Edit
                              </Dropdown.Item>
                              {/* <Dropdown.Item
                                onClick={() =>
                                  navigate(`/vendors/${admin?._id}`)
                                }
                              >
                                Wishlist Vendors
                              </Dropdown.Item> */}
                              <Dropdown.Item
                                onClick={() =>
                                  triggerConfirm(
                                    "status",
                                    admin._id,
                                    admin.isBlocked ? "Enable" : "Suspend"
                                  )
                                }
                              >
                                {admin.isBlocked ? "Enable" : "Suspend"}
                              </Dropdown.Item>
                              <Dropdown.Item
                                onClick={() => triggerConfirm("delete", admin._id)}
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No data found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Tab>

        {/* VENDORS TAB */}
        <Tab eventKey="Vendor" title="Vendors">
          {loading ? (
            <Spinner />
          ) : (
            <div className="table-responsive">
              <table className="table table-striped align-middle shadow-sm">
                <thead className="table-light">
                  <tr>
                    <th>Sr No.</th>
                    <th>Vendor Name</th>
                    <th>Email</th>
                    <th>Phone</th>
                    <th>Joined Date</th>
                    <th>Category</th>
                    <th>Request</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {admins?.length > 0 ? (
                    admins.map((vendor, index) => (
                      <tr key={vendor._id || index}>
                        <td>{currentPage * itemsPerPage + index + 1}</td>
                        <td>
                          {vendor?.name
                            ? `${vendor.name
                              .charAt(0)
                              .toUpperCase()}${vendor.name.slice(1)}`
                            : "_"}
                        </td>
                        <td>{vendor?.email ? vendor?.email : "_"}</td>
                        <td>
                          {vendor?.phoneNumber
                            ? `${vendor?.countryCode || ""} ${vendor?.phoneNumber
                            }`
                            : "_"}
                        </td>
                        <td>
                          {new Date(vendor.createdAt).toLocaleDateString(
                            "en-CA",
                            {
                              day: "2-digit",
                              month: "short",
                              year: "numeric",
                            }
                          )}{" "}
                          {","}
                          {new Date(vendor.createdAt).toLocaleTimeString(
                            "en-CA",
                            {
                              hour: "2-digit",
                              minute: "2-digit",
                            }
                          )}
                        </td>
                        <td>
                          {vendor.businessDetails?.category ? (
                            <span
                              className={`badge bg-${getCategoryColor(
                                vendor.businessDetails.category
                              )}`}
                            >
                              {vendor?.businessDetails?.category?.name
                                .charAt(0)
                                .toUpperCase() +
                                vendor?.businessDetails?.category?.name.slice(
                                  1
                                )}
                            </span>
                          ) : (
                            "_"
                          )}
                        </td>
                        <td>
                          <span
                            className={`badge ${vendor?.adminApprove === "Pending"
                                ? "bg-warning "
                                : vendor?.adminApprove === "Approve" ||
                                  vendor?.adminApprove === "Approved"
                                  ? "bg-success"
                                  : vendor?.adminApprove === "Reject"
                                    ? "bg-danger"
                                    : "bg-secondary"
                              }`}
                          >
                            {vendor?.adminApprove || "N/A"}
                          </span>
                        </td>

                        {vendor?.adminApprove === "Approved" ? <td>
                          <span
                            className={`badge ${!vendor.isBlocked ? "bg-success" : "bg-danger"
                              }`}
                          >
                            {!vendor.isBlocked ? "Active" : "Suspended"}
                          </span>
                        </td>
                          : <td>_</td>}


                        <td style={{ textAlign: "center" }}>
                          <Dropdown>
                            <Dropdown.Toggle
                              variant="light"
                              id={`dropdown-basic-${index}`}
                              style={{ padding: "4px 8px" }}
                            >
                              {svg1}
                            </Dropdown.Toggle>

                            <Dropdown.Menu>
                              <Dropdown.Item onClick={() => openModal(vendor)}>
                                Edit
                              </Dropdown.Item>
                              <Dropdown.Item onClick={() => navigate(`/user-detail/${vendor?._id}`)}>
                                view
                              </Dropdown.Item>

                              <Dropdown.Item
                                onClick={() => openCategoryModal(vendor)}
                              >
                                Change Category
                              </Dropdown.Item>
                              {vendor?.adminApprove === "Approved" ? <Dropdown.Item
                                onClick={() =>
                                  triggerConfirm(
                                    "status",
                                    vendor._id,
                                    vendor.isBlocked ? "Enable" : "Suspend"
                                  )
                                }
                              >
                                {vendor.isBlocked ? "Enable" : "Suspend"}
                              </Dropdown.Item> : ""}



                              {vendor.adminApprove === "Pending" ? (
                                <>
                                  <Dropdown.Item
                                    onClick={() =>
                                      triggerConfirm(
                                        "request",
                                        vendor._id,
                                        "Approved"
                                      )
                                    }
                                  >
                                    Approve
                                  </Dropdown.Item>
                                  <Dropdown.Item
                                    onClick={() =>
                                      triggerConfirm(
                                        "request",
                                        vendor._id,
                                        "Reject"
                                      )
                                    }
                                  >
                                    Reject
                                  </Dropdown.Item>
                                </>
                              ) : vendor.adminApprove === "Approved" ? (
                                <Dropdown.Item
                                  onClick={() =>
                                    triggerConfirm(
                                      "request",
                                      vendor._id,
                                      "Reject"
                                    )
                                  }
                                >
                                  Reject
                                </Dropdown.Item>
                              ) : (
                                <Dropdown.Item
                                  onClick={() =>
                                    triggerConfirm(
                                      "request",
                                      vendor._id,
                                      "Approved"
                                    )
                                  }
                                >
                                  Approve
                                </Dropdown.Item>
                              )}

                              <Dropdown.Item
                                onClick={() =>
                                  triggerConfirm("delete", vendor._id)
                                }
                              >
                                Delete
                              </Dropdown.Item>
                            </Dropdown.Menu>
                          </Dropdown>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="7" className="text-center">
                        No vendors found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </Tab>
      </Tabs>

      <div className="card-footer clearfix mt-3">
        <div className="d-flex flex-column flex-md-row justify-content-between align-items-center text-center text-md-start">
          <div
            className="dataTables_info mb-3 mb-md-0"
            style={{ color: "#64CAE5" }}
          ></div>
          <div className="d-flex align-items-center">
            {totalPages > 0 && (
              <Pagination
                pageCount={totalPages}
                pageValue={currentPage}
                setPage={setCurrentPage}
              />
            )}
          </div>
        </div>
      </div>

      {/* Main Edit/Add Modal */}
      <Modal size="xl" show={showModal} onHide={handleCloseModal}>
        <Modal.Header closeButton>
          <Modal.Title>{selectedAdmin ? "Edit User" : "Add User"}</Modal.Title>
        </Modal.Header>
        <Formik
          initialValues={
            selectedAdmin
              ? {
                userId: selectedAdmin._id,
                name: selectedAdmin.name || "",
                image: selectedAdmin.image || "",
              }
              : {
                name: "",
                image: "",
              }
          }
          enableReinitialize
          validationSchema={AdminSchema}
          onSubmit={handleSubmit}
        >
          {({ errors, touched, values, setFieldValue }) => (
            <FormikForm>
              <Modal.Body>
                <div className="mb-3">
                  <label className="form-label">Name</label>
                  <Field
                    name="name"
                    placeholder="ex. John Doe"
                    className={`form-control ${errors.name && touched.name ? "is-invalid" : ""
                      }`}
                  />
                  <ErrorMessage
                    name="name"
                    component="div"
                    className="text-danger"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Image</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="form-control"
                    ref={fileInputRef}
                    onChange={(e) =>
                      handleFileUpload(e, "image", setFieldValue)
                    }
                  />
                  <ErrorMessage
                    name="image"
                    component="div"
                    className="text-danger mt-1"
                  />
                </div>

                {uploading ? (
                  <div className="text-success mt-2">Uploading image...</div>
                ) : imageLoading ? (
                  <div>loading...</div>
                ) : (
                  preview && (
                    <div className="mb-3 text-center mt-3">
                      <div
                        style={{
                          display: "inline-block",
                          position: "relative",
                        }}
                      >
                        <img
                          src={preview}
                          alt="preview"
                          style={{
                            width: "120px",
                            height: "120px",
                            objectFit: "cover",
                            borderRadius: "10px",
                            border: "1px solid #ddd",
                          }}
                        />
                        <button
                          type="button"
                          className="btn btn-danger btn-sm shadow"
                          style={{
                            position: "absolute",
                            top: "-8px",
                            right: "-8px",
                            borderRadius: "50%",
                            width: "24px",
                            height: "24px",
                            padding: "0",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            lineHeight: "1",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                          onClick={() => {
                            setPreview("");
                            if (fileInputRef.current)
                              fileInputRef.current.value = "";
                            setFieldValue("image", "");
                          }}
                        >
                          &times;
                        </button>
                      </div>
                    </div>
                  )
                )}
              </Modal.Body>
              <Modal.Footer>
                <Button variant="secondary" onClick={handleCloseModal}>
                  Close
                </Button>
                <Button
                  type="submit"
                  style={{ backgroundColor: " #64CAE5", border: "none" }}
                  disabled={uploading}
                >
                  {submit ? "Saving" : "Save Changes"}
                </Button>
              </Modal.Footer>
            </FormikForm>
          )}
        </Formik>
      </Modal>

      {/* 🌟 New Confirmation Modal */}
      <Modal
        show={confirmState.show}
        onHide={() => setConfirmState({ ...confirmState, show: false })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirm Action</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-0 fs-5">{confirmState.message}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setConfirmState({ ...confirmState, show: false })}
          >
            Cancel
          </Button>
          <Button
            variant={
              confirmState.action === "delete" ||
                confirmState.status === "Reject"
                ? "danger"
                : "primary"
            }
            style={
              confirmState.action !== "delete" &&
                confirmState.status !== "Reject"
                ? {
                  backgroundColor: "#64CAE5",
                  borderColor: "#64CAE5",
                  color: "white",
                }
                : {}
            }
            onClick={confirmActionHandler}
          >
            Confirm
          </Button>
        </Modal.Footer>
      </Modal>

      {/* 🌟 New Change Category Modal */}
      <Modal
        show={showCategoryModal}
        onHide={closeCategoryModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Change Category</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            <label className="form-label fw-bold">Select a Category</label>
            <select
              className="form-select"
              value={newCategory}
              onChange={(e) => setNewCategory(e.target.value)}
              style={{ borderRadius: "0.5rem", borderColor: "#64CAE5" }}
            >
              <option value="">-- Choose Category --</option>
              {categories.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={closeCategoryModal}>
            Cancel
          </Button>
          <Button
            variant="primary"
            style={{ backgroundColor: "#64CAE5", borderColor: "#64CAE5" }}
            onClick={handleCategorySubmit}
            disabled={updatingCategory}
          >
            {updatingCategory ? "Saving..." : "Save Category"}
          </Button>
        </Modal.Footer>
      </Modal>

    </div>
  );
};

export default Users;